import { useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Mention, MentionBlot } from "quill-mention";
import "./mention.css";
import "./editor.css";

// Register the mention blot/module against react-quill-new's Quill instance.
try {
	Quill.register({
		"blots/mention": MentionBlot,
		"modules/mention": Mention,
	});
} catch {
	// already registered (hot reload)
}

export interface MentionMember {
	id: number | string;
	display: string;
}

interface RichPostEditorProps {
	/** HTML value (mention spans, NOT yet serialized to tokens). */
	value?: string;
	onChange?: (html: string) => void;
	members: MentionMember[];
	placeholder?: string;
	readOnly?: boolean;
	/** Uploads a File and resolves to the public URL to embed. */
	onImageUpload?: (file: File) => Promise<string>;
}

/**
 * Convert the editor's HTML (with quill-mention spans) to the stored format the
 * backend/notifications expect: "@name [uid: id]" tokens inline, formatting
 * preserved (#1269).
 */
export function quillToStored(html: string): string {
	const doc = new DOMParser().parseFromString(html, "text/html");
	doc.querySelectorAll("span.mention").forEach((span) => {
		const id = span.getAttribute("data-id");
		const value = span.getAttribute("data-value");
		span.replaceWith(doc.createTextNode(`@${value} [uid: ${id}]`));
	});
	return doc.body.innerHTML;
}

/**
 * Inverse of quillToStored: turn stored "@name [uid: id]" tokens back into
 * quill-mention spans so an edited post shows mentions as chips, not raw text.
 */
export function storedToQuill(html: string): string {
	return (html ?? "").replace(
		/@([^[]*?)\s*\[uid:\s*(\d+)\]/gi,
		(_m, name: string, id: string) => {
			const clean = name.trim();
			return `<span class="mention" data-denotation-char="@" data-id="${id}" data-value="${clean}"><span contenteditable="false"><span class="ql-mention-denotation-char">@</span>${clean}</span></span>`;
		},
	);
}

function RichPostEditor({
	value,
	onChange,
	members,
	placeholder,
	readOnly,
	onImageUpload,
}: RichPostEditorProps) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const quillRef = useRef<any>(null);

	const modules = useMemo(
		() => ({
			toolbar: {
				container: [
					["bold", "italic", "underline", "strike"],
					[{ list: "ordered" }, { list: "bullet" }],
					["link", ...(onImageUpload ? ["image"] : [])],
				],
				handlers: onImageUpload
					? {
							image: () => {
								const input = document.createElement("input");
								input.setAttribute("type", "file");
								input.setAttribute("accept", "image/*");
								input.click();
								input.onchange = async () => {
									const file = input.files?.[0];
									if (!file) return;
									const quill = quillRef.current?.getEditor();
									if (!quill) return;
									const range =
										quill.getSelection(true) ??
										{ index: quill.getLength(), length: 0 };
									try {
										const url = await onImageUpload(file);
										quill.insertEmbed(
											range.index,
											"image",
											url,
										);
										quill.setSelection(range.index + 1, 0);
									} catch {
										// error already surfaced by PostForm's handler
									}
								};
							},
					  }
					: {},
			},
			mention: {
				allowedChars: /^[A-Za-z0-9_ .'-]*$/,
				mentionDenotationChars: ["@"],
				spaceAfterInsert: true,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				source: (searchTerm: string, renderList: any) => {
					const term = searchTerm.toLowerCase();
					const matches = members
						.filter((m) => m.display.toLowerCase().includes(term))
						.slice(0, 10)
						.map((m) => ({ id: m.id, value: m.display }));
					renderList(matches, searchTerm);
				},
			},
			clipboard: { matchVisual: false },
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[members, onImageUpload],
	);

	return (
		<div className="rich-post-editor">
			<ReactQuill
				ref={quillRef}
				theme="snow"
				value={value || ""}
				onChange={onChange}
				placeholder={placeholder}
				readOnly={readOnly}
				modules={modules}
			/>
		</div>
	);
}

export default RichPostEditor;
